import { Box, Skeleton, keyframes } from '@mui/material';

const shimmer = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.7; }
  100% { opacity: 0.4; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.01); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.6; }
`;

/**
 * Skeleton placeholder animado moderno.
 * Ocupa todo el espacio disponible del contenido con una simulación
 * de la estructura de página (título, filtros, tabla).
 */
export default function LoadingSkeleton() {
  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        p: { xs: 2, md: 3 },
        animation: `${pulse} 2s ease-in-out infinite`,
      }}
    >
      {/* Barra de título + botón */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton
          variant="rounded"
          width="38%"
          height={38}
          animation="wave"
          sx={{ bgcolor: '#e0e4df' }}
        />
        <Skeleton
          variant="rounded"
          width={140}
          height={38}
          animation="wave"
          sx={{ bgcolor: '#c8d4c3', borderRadius: 2 }}
        />
      </Box>

      {/* Filtros */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          p: 2,
          borderRadius: 2,
          bgcolor: '#f7f8f7',
          border: '1px solid #e8ebe7',
        }}
      >
        {[180, 160, 180, 140].map((w, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width={w}
            height={44}
            animation="wave"
            sx={{
              bgcolor: '#eaede9',
              borderRadius: 1.5,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </Box>

      {/* Tabla */}
      <Box
        sx={{
          flex: 1,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e8ebe7',
          bgcolor: '#fafbfa',
        }}
      >
        {/* Header de tabla */}
        <Skeleton
          variant="rectangular"
          height={46}
          animation="wave"
          sx={{
            bgcolor: '#c8d4c3',
            borderRadius: 0,
          }}
        />

        {/* Filas */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #f0f1f0',
                bgcolor: i % 2 === 0 ? '#ffffff' : '#f9faf9',
                animation: `${shimmer} 1.8s ease-in-out infinite`,
                animationDelay: `${i * 0.08}s`,
              }}
            >
              <Skeleton
                variant="rounded"
                width="12%"
                height={18}
                animation="wave"
                sx={{ bgcolor: '#e4e7e3' }}
              />
              <Skeleton
                variant="rounded"
                width="22%"
                height={18}
                animation="wave"
                sx={{ bgcolor: '#e4e7e3' }}
              />
              <Skeleton
                variant="rounded"
                width="18%"
                height={18}
                animation="wave"
                sx={{ bgcolor: '#e4e7e3' }}
              />
              <Skeleton
                variant="rounded"
                width="15%"
                height={18}
                animation="wave"
                sx={{ bgcolor: '#e4e7e3' }}
              />
              <Skeleton
                variant="rounded"
                width="10%"
                height={18}
                animation="wave"
                sx={{ bgcolor: '#e4e7e3' }}
              />
              <Box sx={{ ml: 'auto' }}>
                <Skeleton
                  variant="circular"
                  width={28}
                  height={28}
                  animation="wave"
                  sx={{ bgcolor: '#dce0db' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Paginación */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 1 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="circular"
            width={32}
            height={32}
            animation="wave"
            sx={{ bgcolor: '#e0e4df' }}
          />
        ))}
      </Box>
    </Box>
  );
}
